import { useCallback, useEffect, useMemo, useState } from "react"
import PQueue from "p-queue"

import { DocumentCategoryWithDocuments, IGetDocumentCategoryWithDocumentsDto } from "models/DocumentCategoryWithDocuments"
import ApiService from "services/ApiService"
import toastMessages from "stores/toastMessages"
import { Guid, IApiLink, ICreateFile } from "@/types"
import FileService from "services/FileService"
import AssetService from "services/AssetService"
import { ApiLinkEntity } from "config/constants"
import { orderBy, uniq } from "lodash"
import UserService from "services/UserService"
import { Document } from "models/Document"

interface IUseDocumentsParams {
    entity: ApiLinkEntity
    documentCategoryId?: Guid
    withEmails?: boolean
    linkGetDocuments?: IApiLink
    linkAddDocuments?: IApiLink
}

export default function useDocuments({
    entity,
    documentCategoryId,
    withEmails,
    linkGetDocuments,
    linkAddDocuments,
}: IUseDocumentsParams) {
    const [allDocumentsByCategory, setAllDocumentsByCategory] = useState<DocumentCategoryWithDocuments[] | null>(null)
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(true)

    const documentsByCategory = useMemo(() => {
        return allDocumentsByCategory?.filter(dc => (documentCategoryId ? dc.id === documentCategoryId : true) && (dc.isReserved || dc.documents.length))
    }, [documentCategoryId, allDocumentsByCategory])

    const fileIds = useMemo(() => {
        return documentsByCategory?.flatMap(dc => dc.documents.map(d => d.id)) || []
    }, [documentsByCategory])

    const hasDocuments = useMemo(() => {
        if (!allDocumentsByCategory) return null
        return allDocumentsByCategory.some(dc => dc.documents.length)
    }, [allDocumentsByCategory])

    const documentCategoryFilterOptions = useMemo(() => {
        if (!allDocumentsByCategory) {
            return []
        }
        return allDocumentsByCategory.filter(c => c.isReserved || c.documents.length).map(c => ({
            value: c.id,
            label: c.name,
        }))
    }, [allDocumentsByCategory])

    const documentCategorySelectOptions = useMemo(() => {
        if (!allDocumentsByCategory) {
            return []
        }
        return allDocumentsByCategory.map(c => ({
            value: c.id,
            label: c.name,
        }))
    }, [allDocumentsByCategory])

    const fetchDocuments = useCallback(async () => {
        if (!linkGetDocuments) {
            console.error("No link to get documents found")
            toastMessages.showSystemError()
            return
        }
        setIsLoadingDocuments(true)
        try {
            const dto: IGetDocumentCategoryWithDocumentsDto = await ApiService.followLink({
                link: linkGetDocuments,
            })
            let userEmails: Record<Guid, string> = {}
            if (withEmails) {
                const userIds = uniq(dto.entities.flatMap(dc => dc.documents.map(d => d.uploadedBy)))
                if (userIds.length) {
                    userEmails = await UserService.getUserEmailsByIds(userIds)
                }
            }
            setAllDocumentsByCategory(dto.entities.map(dc => new DocumentCategoryWithDocuments(dc, { entity, userEmails })))
        } catch (e) {
            console.log(e)
            toastMessages.showSystemError()
        }
        setIsLoadingDocuments(false)
    }, [linkGetDocuments, withEmails, entity])

    useEffect(() => {
        fetchDocuments()
    }, [fetchDocuments])

    async function uploadDocuments(params: { documentCategoryId?: Guid, files: File[] }) {
        if (!linkAddDocuments || !params.documentCategoryId) {
            throw new Error()
        }

        const queue = new PQueue({ concurrency: 1 })
        const uploadedFileIds: string[] = []

        const uploadFile = async (file: File) => {
            try {
                const uploadedFile = await FileService.uploadFile<ICreateFile>(file)
                uploadedFileIds.push(uploadedFile.id)
            } catch (e) {
                toastMessages.showSystemError()
            }
        }

        for (const file of params.files) {
            queue.add(uploadFile.bind(null, file))
        }

        await queue.onIdle()

        if (uploadedFileIds.length === 0) {
            throw new Error()
        }

        await AssetService.addDocuments(linkAddDocuments, {
            documentCategoryId: params.documentCategoryId,
            fileIds: uploadedFileIds,
        })

        fetchDocuments()
    }

    function moveDocumentToCategory(document: Document, newCategoryId: Guid) {
        if (!allDocumentsByCategory) return
        setAllDocumentsByCategory(allDocumentsByCategory.map(dc => {
            if (dc.id === newCategoryId) {
                dc.documents.push(document)
                dc.documents = orderBy(dc.documents, d => d.uploadDate.valueOf(), "asc")
            } else {
                dc.documents = dc.documents.filter(d => d.id !== document.id)
            }
            return dc
        }))

    }

    return {
        hasDocuments,
        documentsByCategory,
        documentCategoryFilterOptions,
        documentCategorySelectOptions,
        isLoadingDocuments,
        fileIds,
        uploadDocuments,
        refetchDocuments: fetchDocuments,
        moveDocumentToCategory,
    }
}
